import React, { useEffect, useRef, useState } from "react";

import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAiStatus } from "../contexts/AiStatusContext";

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const aiStatus = useAiStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (menuOpen && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Функция для определения активной ссылки
  const isActiveLink = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  // Функция для получения классов активной ссылки
  const getLinkClasses = (path: string) => {
    const baseClasses =
      "px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg";
    const isActive = isActiveLink(path);

    if (isActive) {
      return `${baseClasses} bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 shadow-sm`;
    }

    return `${baseClasses} text-gray-700 hover:text-gray-900 hover:bg-gray-50 border border-transparent`;
  };

  const shouldHideNav = location.pathname === "/choose-plan";

  return (
    <div className="min-h-[calc(100vh-65px)] bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 shadow-sm border-b z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-8">
              <Link
                to="/"
                className="text-xl font-extrabold tracking-tight cursor-pointer"
              >
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  RevuAI
                </span>
              </Link>

              {/* Navigation Menu */}
              {!shouldHideNav && (
                <nav className="flex space-x-6">
                  <Link to="/reviews" className={getLinkClasses("/reviews")}>
                    Отзывы
                  </Link>
                  <Link to="/stores" className={getLinkClasses("/stores")}>
                    Магазины
                  </Link>
                  <Link
                    to="/templates"
                    className={getLinkClasses("/templates")}
                  >
                    Шаблоны
                  </Link>
                  {/* removed OZON products link */}
                </nav>
              )}
            </div>

            <div className="relative flex items-center space-x-3" ref={menuRef}>
              {/* AI indicator in header (subtle) */}
              <div
                className="flex items-center gap-1.5 px-2.5 h-8 rounded-full border border-gray-200 bg-white/70"
                title={
                  aiStatus?.message ||
                  (aiStatus?.isConnected ? "AI подключен" : "Нет связи с AI")
                }
                aria-label={
                  aiStatus?.isConnected ? "AI подключен" : "AI недоступен"
                }
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    aiStatus == null
                      ? "bg-gray-300 animate-pulse"
                      : aiStatus.isConnected
                        ? "bg-emerald-500"
                        : "bg-gray-400"
                  }`}
                />
                <span className="text-xs font-medium text-gray-600">AI</span>
              </div>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center justify-center h-10 w-10 rounded-full bg-primary-100 text-primary-700"
                aria-label="User menu"
              >
                {(user?.firstName || user?.email || "U")
                  .charAt(0)
                  .toUpperCase()}
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 md:w-max bg-white/95 backdrop-blur rounded-xl border border-gray-200 shadow-xl ring-1 ring-black/5 py-1.5 z-50">
                  {!shouldHideNav && (
                    <>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setMenuOpen(false)}
                      >
                        Профиль
                      </Link>
                      {user?.role === "owner" && (
                        <Link
                          to="/store-management"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 md:whitespace-nowrap"
                          onClick={() => setMenuOpen(false)}
                        >
                          Управление магазинами
                        </Link>
                      )}
                      {user?.role === "owner" && (
                        <Link
                          to="/org-users"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 md:whitespace-nowrap"
                          onClick={() => setMenuOpen(false)}
                        >
                          Управление пользователями
                        </Link>
                      )}
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setMenuOpen(false)}
                      >
                        Настройки
                      </Link>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Выйти
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with top padding to account for fixed header */}
      <main
        className={`max-w-7xl mx-auto ${shouldHideNav ? "pt-[65px] mt-0 pb-0" : "py-6 mt-16"} sm:px-6 lg:px-8`}
      >
        <Outlet />
      </main>
    </div>
  );
};
